import { yupResolver } from '@hookform/resolvers/yup';
import {
  TextField, Button, FormControl, InputLabel, MenuItem, Select,
  Typography, Grid, CircularProgress, Alert, Box
} from '@mui/material';
import { Controller, useForm, SubmitHandler } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';

// Tus hooks y tipos
import { useUserCore } from '../../../../core/modules/user';
import { useGlobalCore } from '../../../../core/modules/global';
import { COUNTRY } from '../../../../core/constants/country';
import { CompanyRole } from '../../../../core/modules/user/interfaces/role';
import { PutUserPayload } from '../../../../core/modules/user/interfaces/put-user';


// 1. Interfaz de datos del formulario actualizada
interface FormData {
  fullName: string;
  email: string;
  role: CompanyRole;
  city: string;
  country: string;
  phone_number: string;
}

// 2. Esquema de validación actualizado
const schema = yup.object().shape({
  fullName: yup.string().required('Debes ingresar un nombre.'),
  email: yup.string().email('El formato del email no es correcto.').required('Debes ingresar un email.'),
  role: yup.string().oneOf(Object.values(CompanyRole)).required('Debes seleccionar un rol.'),
  city: yup.string(),
  country: yup.string(),
  phone_number: yup.string(),
});

const BasicInfoTab = () => {
  const { t } = useTranslation();
  const { user, putUser, getUser } = useUserCore();
  const { showSnackbar } = useGlobalCore();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const { control, formState, handleSubmit, reset } = useForm<FormData>({
    mode: 'onChange'
  });
  const { isValid, errors, isDirty } = formState;

  // 3. Usar `reset` para poblar el formulario de forma eficiente
  useEffect(() => {
    if (user) {
      reset({
        fullName: user.full_name || '',
        email: user.email || '',
        role: user.role,
        city: user.city || '',
        country: user.country || '',
        phone_number: user.phone_number || ''
      });
    }
  }, [user, reset]);

  const onSubmit: SubmitHandler<FormData> = useCallback(
    async (formData) => {
      if (!user?.id) return;
      
      setIsSubmitting(true);
      setApiError(null);

      // El payload solo incluye los campos que se pueden editar en este formulario
      const payload: PutUserPayload = {
        fullName: formData.fullName,
        email: formData.email,
        role: formData.role,
        city: formData.city,
        country: formData.country,
        phone_number: formData.phone_number,
      };

      await putUser(user.id, payload,
        () => {
          showSnackbar({ message: 'Usuario actualizado con éxito', options: { variant: 'success' } });
          getUser(user.id);
        },
        (error: any) => {
            const message = error.response?.data?.detail || 'Ocurrió un error al actualizar.';
            setApiError(message);
        }
      );
      
      setIsSubmitting(false);
    },
    [user, putUser, getUser, showSnackbar]
  );

  if (!user) {
    return <CircularProgress />; // Muestra un loader si el usuario aún no ha cargado
  }

  return (
    <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Información Básica
      </Typography>
      {apiError && <Alert severity="error" sx={{ mb: 2 }}>{apiError}</Alert>}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Controller name="fullName" control={control} render={({ field }) => (
            <TextField {...field} label={t('label.name')} error={!!errors.fullName} helperText={errors.fullName?.message} fullWidth required />
          )} />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth error={!!errors.role}>
            <InputLabel id="user-role-label">{t('label.role')}</InputLabel>
            <Controller name="role" control={control} render={({ field }) => (
                <Select {...field} labelId="user-role-label" label={t('label.role')} required disabled={isSubmitting || user.is_superuser}>
                  {/* Un superadmin no puede cambiar su propio rol */}
                  {Object.values(CompanyRole).map((roleValue) => (
                    <MenuItem key={roleValue} value={roleValue}>{roleValue}</MenuItem>
                  ))}
                </Select>
            )} />
            {errors.role && <Typography variant="caption" color="error">{errors.role.message}</Typography>}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
            <Controller name="email" control={control} render={({ field }) => (
                <TextField {...field} label="Email" type="email" error={!!errors.email} helperText={errors.email?.message} required fullWidth />
            )} />
        </Grid>
        
        <Grid item xs={12} md={6}>
            <Controller name="phone_number" control={control} render={({ field }) => (
                <TextField {...field} label={t('label.phone')} error={!!errors.phone_number} helperText={errors.phone_number?.message} fullWidth />
            )} />
        </Grid>

        {/* ... puedes añadir los campos de país y ciudad aquí si es necesario ... */}

        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={!isDirty || !isValid || isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isSubmitting ? 'Guardando...' : t('button.save')}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BasicInfoTab;